/**
 * Typed accessors for design tokens. Frontends import the CSS
 * (`@mb-group/design-tokens/css`) for the custom properties and use these
 * helpers when they need token names in TypeScript (e.g. inline styles, canvas).
 */
import type { ThemeMode } from "@mb-group/shared-types";

/** Friendly name → CSS custom-property name for every semantic design token. */
export const SEMANTIC_TOKENS = {
	bg: "--mb-bg",
	bgElevated: "--mb-bg-elevated",
	bgSunken: "--mb-bg-sunken",
	text: "--mb-text",
	textMuted: "--mb-text-muted",
	brand: "--mb-brand",
	brandSoft: "--mb-brand-soft",
	accent: "--mb-accent",
	border: "--mb-border",
	hairline: "--mb-hairline",
	focusRing: "--mb-focus-ring",
	radiusLg: "--mb-radius-lg",
	containerMax: "--mb-container-max",
} as const;

/** Union of the CSS custom-property names in {@link SEMANTIC_TOKENS}. */
export type SemanticTokenName = (typeof SEMANTIC_TOKENS)[keyof typeof SEMANTIC_TOKENS];

/** Read a resolved token value from an element (defaults to :root). */
export function readToken(name: SemanticTokenName, el: Element | null = null): string {
	const target = el ?? (typeof document !== "undefined" ? document.documentElement : null);
	if (!target) return "";
	return getComputedStyle(target).getPropertyValue(name).trim();
}

/**
 * Apply the theme to the document root (used by the app's theme switcher).
 *
 * There is a single brand skin, so this only writes `data-theme`. The former
 * `data-brand` attribute went away with the V1/V2 switch (ADR 0012).
 */
export function applyThemeAttributes(theme: ThemeMode): void {
	if (typeof document === "undefined") return;
	document.documentElement.setAttribute("data-theme", theme);
}
